import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Heart, Shield, Clock, Video, Phone, Calendar, CheckCircle, 
  ArrowRight, ChevronDown, ChevronUp, Users, Star, Lock,
  Brain, MessageCircle, Smile, UserCheck, Home, Sun,
  Moon, Zap, AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MentalHealthServices = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GEWO Brand Colors
  const colors = {
    primaryBlue: '#205BF3',
    deepBlue: '#1A4AC5',
    accentGold: '#FED07B',
    white: '#FFFFFF',
    text: '#0B1220',
    mutedText: '#475569',
    softBackground: '#F3F7FF'
  };

  // Services Data - 8 cards
  const services = [
    {
      icon: '😰',
      title: 'Anxiety & Stress',
      description: 'Evidence-based treatment for generalized anxiety, social anxiety, panic disorders, and chronic stress management.'
    },
    {
      icon: '🌧️',
      title: 'Depression Support',
      description: 'Compassionate care for major depression, persistent depressive disorder, and mood-related challenges.'
    },
    {
      icon: '💔',
      title: 'Trauma & PTSD',
      description: 'Specialized trauma-focused therapy including EMDR and cognitive processing for healing and recovery.'
    },
    {
      icon: '🎯',
      title: 'ADHD & Focus Support',
      description: 'Comprehensive assessment and management strategies for attention and executive function challenges.'
    },
    {
      icon: '💑',
      title: 'Couples Therapy',
      description: 'Strengthen relationships through improved communication, conflict resolution, and emotional connection.'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Family Therapy',
      description: 'Navigate family dynamics, improve communication, and build healthier relationships together.'
    },
    {
      icon: '🕊️',
      title: 'Grief Counseling',
      description: 'Supportive guidance through loss and bereavement with compassionate, personalized care.'
    },
    {
      icon: '😴',
      title: 'Sleep & Insomnia Support',
      description: 'Address sleep difficulties with cognitive behavioral therapy for insomnia (CBT-I) and holistic approaches.'
    }
  ];

  // How It Works - 3 steps
  const steps = [
    {
      number: '01',
      icon: Heart,
      title: 'Choose a Service',
      description: 'Browse our range of mental health services and select the one that best fits your needs.'
    },
    {
      number: '02',
      icon: Calendar,
      title: 'Schedule Your Session',
      description: 'Book a convenient time for your appointment—in-person or via secure telehealth.'
    },
    {
      number: '03',
      icon: CheckCircle,
      title: 'Begin Your Personalized Plan',
      description: 'Work with your provider to create a treatment plan tailored to your goals and wellbeing.'
    }
  ];

  // Who We Help
  const whoWeHelp = [
    { icon: '🧑‍🎓', label: 'Students' },
    { icon: '👩‍💼', label: 'Working Professionals' },
    { icon: '💑', label: 'Couples' },
    { icon: '👨‍👩‍👧', label: 'Families' },
    { icon: '🧓', label: 'Seniors' },
    { icon: '👶', label: 'Teens & Adolescents' },
    { icon: '🩺', label: 'Caregivers' },
    { icon: '🌍', label: 'Adults of All Backgrounds' }
  ];

  // Why Choose GEWO
  const whyChoose = [
    { icon: CheckCircle, text: 'Evidence-based care rooted in the latest research' },
    { icon: UserCheck, text: 'Personalized approach tailored to your unique needs' },
    { icon: Lock, text: 'Confidential & secure—your privacy is our priority' },
    { icon: Clock, text: 'Flexible scheduling for busy lifestyles' },
    { icon: Users, text: 'Culturally sensitive support from diverse providers' },
    { icon: Video, text: 'Telehealth options for care from anywhere' }
  ];

  // FAQ Data
  const faqs = [
    {
      question: 'What happens in the first session?',
      answer: "Your first session is an opportunity for you and your provider to get to know each other. You'll discuss your concerns, goals, and history. Together, you'll start building a personalized treatment plan. There's no pressure—it's a safe space to share at your own pace."
    },
    {
      question: 'How long is each session?',
      answer: 'Most individual therapy sessions last 45-60 minutes. Couples and family sessions may be longer, typically 60-90 minutes. Your provider will discuss the best session length for your specific needs.'
    },
    {
      question: 'Is telehealth private?',
      answer: 'Absolutely. Our telehealth platform uses end-to-end encryption and is fully HIPAA-compliant. Your sessions are conducted through a secure, private video connection—just like being in the office, but from wherever you feel most comfortable.'
    },
    {
      question: 'Do you accept insurance?',
      answer: 'We accept many major insurance plans. We recommend contacting us to verify your specific coverage and understand any out-of-pocket costs before your first appointment.'
    },
    {
      question: 'How soon can I be seen?',
      answer: 'We strive to offer appointments within 1-2 weeks of your initial contact. Urgent matters may be accommodated sooner. Call us or book online to find the earliest available time.'
    },
    {
      question: 'Can I switch providers if needed?',
      answer: "Yes, your comfort and progress are our priorities. If you feel another provider might be a better fit, we'll help you transition smoothly without any hassle."
    },
    {
      question: 'What if I need urgent help?',
      answer: "If you're experiencing a mental health crisis, please call 911, go to your nearest emergency room, or contact the 988 Suicide & Crisis Lifeline by calling or texting 988. Our services are not intended for emergency situations."
    },
    {
      question: 'Do you offer ongoing care plans?',
      answer: 'Yes, we provide ongoing care plans for those who benefit from continued support. Your provider will work with you to determine the right frequency and duration of sessions based on your progress and goals.'
    }
  ];

  // Custom Heart Logo SVG (GEWO mark - blue heart with gold cross)
  const GEWOHeartLogo = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={colors.deepBlue} />
          <stop offset="100%" stopColor={colors.primaryBlue} />
        </linearGradient>
      </defs>
      {/* Heart shape */}
      <path 
        d="M40 70C40 70 12 50 12 30C12 18 22 10 34 10C38 10 40 12 40 12C40 12 42 10 46 10C58 10 68 18 68 30C68 50 40 70 40 70Z" 
        fill="url(#heartGradient)"
      />
      {/* Gold cross */}
      <rect x="36" y="26" width="8" height="28" rx="2" fill={colors.accentGold} />
      <rect x="28" y="34" width="24" height="8" rx="2" fill={colors.accentGold} />
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>GEWO Mental Health Services | Compassionate, Confidential Care</title>
        <meta name="description" content="GEWO offers compassionate, confidential mental health care. Book your appointment or request a consultation today. Evidence-based, personalized support." />
      </Helmet>

      <Navbar />

      {/* ========== HERO SECTION ========== */}
      <section style={{
        background: `linear-gradient(135deg, ${colors.deepBlue}, ${colors.primaryBlue})`,
        minHeight: isMobile ? 'auto' : '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '100px 20px 60px' : '140px 40px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(254, 208, 123, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '900px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          {/* GEWO Logo Badge */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <GEWOHeartLogo />
          </div>

          <h1 style={{
            fontSize: isMobile ? '36px' : isSmallMobile ? '28px' : '56px',
            fontWeight: '800',
            color: colors.white,
            marginBottom: '20px',
            lineHeight: '1.15',
            letterSpacing: '-1px'
          }}>
            Mental Health Services
          </h1>

          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '36px',
            maxWidth: '680px',
            margin: '0 auto 36px',
            lineHeight: '1.7'
          }}>
            Compassionate, confidential support for your mental wellbeing. Our licensed providers are here to help you navigate life's challenges with evidence-based care.
          </p>

          {/* CTAs */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '32px'
          }}>
            <button style={{
              background: colors.accentGold,
              color: colors.text,
              border: 'none',
              padding: '16px 36px',
              fontSize: '17px',
              fontWeight: '700',
              borderRadius: '50px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 30px rgba(254, 208, 123, 0.4)',
              transition: 'all 0.3s ease'
            }}>
              <Calendar size={20} />
              Book an Appointment
            </button>
            <button style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              color: colors.white,
              border: `2px solid rgba(255, 255, 255, 0.3)`,
              padding: '14px 32px',
              fontSize: '17px',
              fontWeight: '600',
              borderRadius: '50px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.3s ease'
            }}>
              <MessageCircle size={20} />
              Request a Consultation
            </button>
          </div>

          {/* Emergency Note */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '14px 24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: '600px'
          }}>
            <AlertTriangle size={18} color={colors.accentGold} />
            <span style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'left'
            }}>
              <strong>Not for emergencies:</strong> If you are in immediate danger, call 911 or your local emergency services.
            </span>
          </div>
        </div>
      </section>

      {/* ========== SERVICES WE OFFER ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: `linear-gradient(135deg, ${colors.softBackground}, #E8EFFF)`,
              color: colors.primaryBlue,
              padding: '8px 20px',
              borderRadius: '50px',
              fontSize: '14px',
              fontWeight: '600',
              display: 'inline-block',
              marginBottom: '16px'
            }}>
              Our Specialties
            </span>
            <h2 style={{
              fontSize: isMobile ? '28px' : '44px',
              fontWeight: '800',
              color: colors.text,
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              Services We Offer
            </h2>
            <p style={{
              fontSize: '18px',
              color: colors.mutedText,
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Comprehensive mental health support tailored to your unique journey
            </p>
          </div>

          {/* Services Grid - 8 cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isSmallMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '24px'
          }}>
            {services.map((service, index) => (
              <div key={index} style={{
                background: colors.white,
                border: `1px solid #E5E7EB`,
                borderRadius: '16px',
                padding: '32px 24px',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 20px 40px rgba(32, 91, 243, 0.15)`;
                e.currentTarget.style.borderColor = colors.primaryBlue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px'
                }}>
                  {service.icon}
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: colors.text,
                  marginBottom: '10px'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: colors.mutedText,
                  lineHeight: '1.6'
                }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* Optional Medication Management Note */}
          <p style={{
            textAlign: 'center',
            fontSize: '14px',
            color: colors.mutedText,
            marginTop: '40px',
            fontStyle: 'italic'
          }}>
            *Medication management available if clinically appropriate and determined by your provider.
          </p>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: colors.softBackground
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '44px',
              fontWeight: '800',
              color: colors.text,
              marginBottom: '16px'
            }}>
              How It Works
            </h2>
            <p style={{
              fontSize: '18px',
              color: colors.mutedText,
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              Getting started is simple and stress-free
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '40px',
            position: 'relative'
          }}>
            {/* Connection line (desktop only) */}
            {!isMobile && (
              <div style={{
                position: 'absolute',
                top: '60px',
                left: '20%',
                right: '20%',
                height: '2px',
                background: `linear-gradient(90deg, ${colors.accentGold}, ${colors.primaryBlue}, ${colors.accentGold})`,
                zIndex: 0
              }} />
            )}

            {steps.map((step, index) => (
              <div key={index} style={{
                textAlign: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.deepBlue})`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: `0 12px 40px rgba(32, 91, 243, 0.3)`,
                  position: 'relative'
                }}>
                  <step.icon size={48} color={colors.white} />
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '40px',
                    height: '40px',
                    background: colors.accentGold,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '800',
                    fontSize: '14px',
                    color: colors.text,
                    boxShadow: '0 4px 12px rgba(254, 208, 123, 0.5)'
                  }}>
                    {step.number}
                  </div>
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: colors.text,
                  marginBottom: '12px'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: colors.mutedText,
                  lineHeight: '1.7',
                  maxWidth: '280px',
                  margin: '0 auto'
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHO WE HELP ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '44px',
              fontWeight: '800',
              color: colors.text,
              marginBottom: '16px'
            }}>
              Who We Help
            </h2>
            <p style={{
              fontSize: '18px',
              color: colors.mutedText
            }}>
              Mental health support for everyone
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            justifyContent: 'center'
          }}>
            {whoWeHelp.map((item, index) => (
              <div key={index} style={{
                background: colors.softBackground,
                padding: '16px 28px',
                borderRadius: '50px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: colors.text,
                border: `1px solid #E5E7EB`,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryBlue;
                e.currentTarget.style.color = colors.white;
                e.currentTarget.style.borderColor = colors.primaryBlue;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.softBackground;
                e.currentTarget.style.color = colors.text;
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
              >
                <span style={{ fontSize: '24px' }}>{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE GEWO ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: `linear-gradient(135deg, ${colors.deepBlue}, ${colors.primaryBlue})`
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '44px',
              fontWeight: '800',
              color: colors.white,
              marginBottom: '16px'
            }}>
              Why Choose GEWO
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              Trusted, compassionate care that puts you first
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px'
          }}>
            {whyChoose.map((item, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateX(8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: colors.accentGold,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <item.icon size={24} color={colors.text} />
                </div>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: colors.white,
                  margin: 0
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TELEHEALTH / IN-PERSON CARE ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <span style={{
                background: colors.softBackground,
                color: colors.primaryBlue,
                padding: '8px 20px',
                borderRadius: '50px',
                fontSize: '14px',
                fontWeight: '600',
                display: 'inline-block',
                marginBottom: '16px'
              }}>
                Flexible Care Options
              </span>
              <h2 style={{
                fontSize: isMobile ? '28px' : '40px',
                fontWeight: '800',
                color: colors.text,
                marginBottom: '20px',
                lineHeight: '1.2'
              }}>
                Telehealth & In-Person Sessions
              </h2>
              <p style={{
                fontSize: '17px',
                color: colors.mutedText,
                lineHeight: '1.8',
                marginBottom: '24px'
              }}>
                Whether you prefer the convenience of video sessions from your home or the connection of in-person care, we've got you covered. Our secure telehealth platform is HIPAA-compliant, ensuring your conversations stay private and protected.
              </p>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {[
                  'Secure, encrypted video sessions',
                  'Available on any device—phone, tablet, or computer',
                  'Same quality care as in-person visits',
                  'In-person appointments available at select locations'
                ].map((item, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '15px',
                    color: colors.text
                  }}>
                    <CheckCircle size={20} color={colors.primaryBlue} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${colors.softBackground}, #E8EFFF)`,
              borderRadius: '24px',
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: colors.white,
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.deepBlue})`,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Video size={28} color={colors.white} />
                </div>
                <div>
                  <h4 style={{ fontSize: '17px', fontWeight: '700', color: colors.text, margin: 0 }}>
                    Telehealth Sessions
                  </h4>
                  <p style={{ fontSize: '14px', color: colors.mutedText, margin: '4px 0 0' }}>
                    Secure video from anywhere
                  </p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: colors.white,
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: `linear-gradient(135deg, ${colors.accentGold}, #FFE4A8)`,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Home size={28} color={colors.text} />
                </div>
                <div>
                  <h4 style={{ fontSize: '17px', fontWeight: '700', color: colors.text, margin: 0 }}>
                    In-Person Care
                  </h4>
                  <p style={{ fontSize: '14px', color: colors.mutedText, margin: '4px 0 0' }}>
                    Visit us at select locations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== INSURANCE & PRICING ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '80px 40px',
        background: colors.softBackground
      }}>
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            background: `linear-gradient(135deg, ${colors.primaryBlue}, ${colors.deepBlue})`,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Shield size={36} color={colors.white} />
          </div>
          <h2 style={{
            fontSize: isMobile ? '28px' : '40px',
            fontWeight: '800',
            color: colors.text,
            marginBottom: '20px'
          }}>
            Insurance & Pricing
          </h2>
          <p style={{
            fontSize: '18px',
            color: colors.mutedText,
            lineHeight: '1.8',
            marginBottom: '12px'
          }}>
            We accept many insurance plans and offer self-pay options to make mental health care accessible.
          </p>
          <p style={{
            fontSize: '16px',
            color: colors.mutedText,
            lineHeight: '1.7'
          }}>
            <strong style={{ color: colors.text }}>Contact us</strong> to verify your coverage and discuss availability before your first appointment.
          </p>
          <button style={{
            background: colors.white,
            color: colors.primaryBlue,
            border: `2px solid ${colors.primaryBlue}`,
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '700',
            borderRadius: '50px',
            cursor: 'pointer',
            marginTop: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = colors.primaryBlue;
            e.currentTarget.style.color = colors.white;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = colors.white;
            e.currentTarget.style.color = colors.primaryBlue;
          }}
          >
            <Phone size={18} />
            Verify Coverage
          </button>
        </div>
      </section>

      {/* ========== FAQ SECTION ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: colors.white
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '44px',
              fontWeight: '800',
              color: colors.text,
              marginBottom: '16px'
            }}>
              Frequently Asked Questions
            </h2>
            <p style={{
              fontSize: '18px',
              color: colors.mutedText
            }}>
              Find answers to common questions about our services
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {faqs.map((faq, index) => (
              <div 
                key={index}
                style={{
                  background: colors.white,
                  border: `1px solid ${openFaq === index ? colors.primaryBlue : '#E5E7EB'}`,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  boxShadow: openFaq === index ? `0 8px 30px rgba(32, 91, 243, 0.1)` : '0 2px 8px rgba(0,0,0,0.02)'
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span style={{
                    fontSize: '17px',
                    fontWeight: '700',
                    color: openFaq === index ? colors.primaryBlue : colors.text
                  }}>
                    {faq.question}
                  </span>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    background: openFaq === index ? colors.primaryBlue : colors.softBackground,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginLeft: '16px',
                    transition: 'all 0.3s ease'
                  }}>
                    {openFaq === index ? (
                      <ChevronUp size={20} color={colors.white} />
                    ) : (
                      <ChevronDown size={20} color={colors.mutedText} />
                    )}
                  </div>
                </button>
                {openFaq === index && (
                  <div style={{
                    padding: '0 24px 24px',
                    fontSize: '15px',
                    color: colors.mutedText,
                    lineHeight: '1.8'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA BANNER ========== */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: `linear-gradient(135deg, ${colors.deepBlue}, ${colors.primaryBlue})`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(254, 208, 123, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '800',
            color: colors.white,
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            Take the First Step Today
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '36px',
            maxWidth: '600px',
            margin: '0 auto 36px',
            lineHeight: '1.7'
          }}>
            Your mental health matters. Our compassionate team is ready to support you on your journey to wellness—confidentially and with care.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button style={{
              background: colors.accentGold,
              color: colors.text,
              border: 'none',
              padding: '18px 40px',
              fontSize: '17px',
              fontWeight: '700',
              borderRadius: '50px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 30px rgba(254, 208, 123, 0.4)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(254, 208, 123, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(254, 208, 123, 0.4)';
            }}
            >
              <Calendar size={20} />
              Book Appointment
            </button>
            <button style={{
              background: 'transparent',
              color: colors.white,
              border: `2px solid rgba(255, 255, 255, 0.5)`,
              padding: '16px 36px',
              fontSize: '17px',
              fontWeight: '600',
              borderRadius: '50px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            >
              <Phone size={20} />
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default MentalHealthServices;
